import { useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowDown, ArrowUp, Shuffle, Wifi } from 'lucide-react';
import { type CardData } from '@/components/cards/Card';
import { VerticalCard } from '@/components/cards/VerticalCard';
import { FRAME_META, type FrameKey, type ThemePreset } from '@/lib/themes';
import { cn } from '@/lib/utils';

const DECK: Array<{ frame: FrameKey; card: CardData; palette: ThemePreset }> = [
  { frame: 'cream', palette: { pageBg: '#f5f1e9', cardBg: '#f7f1e5', text: '#14120f', accent: '#e9ad48' }, card: { id: 'hero-cream', slug: 'elena', display_name: 'Elena Marchetti', title: 'Venture Partner', company: 'Vector Capital', theme_preset: 'custom' } },
  { frame: 'blueprint', palette: { pageBg: '#1f49c6', cardBg: '#315ee7', text: '#ffffff', accent: '#dce5ff' }, card: { id: 'hero-blueprint', slug: 'maya', display_name: 'Maya Chen', title: 'Product Lead', company: 'Orbit Works', theme_preset: 'custom' } },
  { frame: 'sticker', palette: { pageBg: '#e9531d', cardBg: '#ff6b2b', text: '#15120f', accent: '#ffe5d8' }, card: { id: 'hero-sticker', slug: 'rio', display_name: 'Rio Alvarez', title: 'Creative Director', company: 'Good Noise', theme_preset: 'custom' } },
  { frame: 'neon', palette: { pageBg: '#c7d20f', cardBg: '#d9e520', text: '#11110d', accent: '#315ee7' }, card: { id: 'hero-neon', slug: 'jules', display_name: 'Jules Martin', title: 'DJ & Producer', company: 'Afterglow', theme_preset: 'custom' } },
  { frame: 'editorial', palette: { pageBg: '#59402c', cardBg: '#765238', text: '#fffaf1', accent: '#e8b459' }, card: { id: 'hero-editorial', slug: 'samira', display_name: 'Samira Okafor', title: 'Architect', company: 'Common Form', theme_preset: 'custom' } },
];

// Shared by the exchange demo so the marketing journey stays visually consistent.
const PORTRAITS = [
  '/marketing/elena-portrait.jpg',
  '/marketing/maya-portrait.jpg',
  '/marketing/rio-portrait.jpg',
  '/marketing/alex-portrait.jpg',
  '/marketing/elena-portrait.jpg',
];

export const HERO_CARD: CardData = { ...DECK[0].card, photo_url: PORTRAITS[0] };

/** A tactile, shufflable stack made from the same cards customers publish. */
export function HeroCard({ className }: { className?: string }) {
  const [active, setActive] = useState(0);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const reduceMotion = useReducedMotion();
  const ordered = useMemo(
    () => DECK.map((item, index) => ({ ...item, sourceIndex: index, slot: (index - active + DECK.length) % DECK.length })),
    [active],
  );

  const shuffle = (direction = 1) => {
    setActive((current) => (current + direction + DECK.length) % DECK.length);
    setTilt({ x: 0, y: 0 });
  };

  return (
    <div className={cn('play-deck', className)}>
      <div
        className="play-deck__stage"
        onPointerMove={(event) => {
          if (reduceMotion || event.pointerType === 'touch') return;
          const bounds = event.currentTarget.getBoundingClientRect();
          setTilt({
            x: ((event.clientY - bounds.top) / bounds.height - 0.5) * -8,
            y: ((event.clientX - bounds.left) / bounds.width - 0.5) * 10,
          });
        }}
        onPointerLeave={() => setTilt({ x: 0, y: 0 })}
      >
        <motion.div
          className="play-deck__stack"
          animate={{ rotateX: tilt.x, rotateY: tilt.y }}
          transition={{ type: 'spring', stiffness: 120, damping: 18 }}
        >
          {[...ordered].sort((a, b) => b.slot - a.slot).map(({ frame, card, palette, sourceIndex, slot }) => {
            const isActive = slot === 0;
            return (
              <motion.button
                key={card.id}
                type="button"
                className="play-deck__card"
                style={{ zIndex: isActive ? 100 : DECK.length - slot }}
                animate={{
                  x: slot * 30,
                  y: slot * -12,
                  z: slot * -42,
                  rotateZ: slot === 0 ? -5 : -5 + slot * 4.5,
                  rotateY: slot * -2,
                  scale: 1 - slot * 0.035,
                  opacity: slot > 3 ? 0.35 : 1,
                }}
                transition={{ type: 'spring', stiffness: 210, damping: 24 }}
                onClick={() => (isActive ? shuffle(1) : setActive(sourceIndex))}
                aria-label={isActive ? `Shuffle ${FRAME_META[frame].name} card` : `Show ${FRAME_META[frame].name} card`}
              >
                <span className="play-deck__edge" aria-hidden="true" />
                <VerticalCard card={{ ...card, photo_url: PORTRAITS[sourceIndex] }} themeOverride={palette} className="play-deck__face w-full shadow-none" />
                {!isActive && (
                  <span
                    className="play-deck__back"
                    style={{ backgroundColor: palette.cardBg, color: palette.text }}
                    aria-hidden="true"
                  >Haphe</span>
                )}
                {isActive && (
                  <span className="play-deck__tap" aria-hidden="true"><Wifi /> tap to share</span>
                )}
              </motion.button>
            );
          })}
        </motion.div>

        <motion.div
          className="play-deck__orbit"
          aria-hidden="true"
          animate={reduceMotion ? undefined : { rotate: 360 }}
          transition={{ duration: 24, repeat: Infinity, ease: 'linear' }}
        ><span /></motion.div>

        <div className="play-deck__rail" aria-label="Choose a card frame">
          <span className="play-deck__rail-label">Shuffle<br />the deck</span>
          <button type="button" onClick={() => shuffle(-1)} aria-label="Previous card"><ArrowUp /></button>
          <div className="play-deck__dots" role="tablist" aria-label="Card frames">
            {DECK.map((item, index) => (
              <button
                key={item.frame}
                type="button"
                role="tab"
                aria-selected={active === index}
                aria-label={FRAME_META[item.frame].name}
                onClick={() => setActive(index)}
                className={active === index ? 'is-active' : ''}
              />
            ))}
          </div>
          <button type="button" onClick={() => shuffle(1)} aria-label="Next card"><ArrowDown /></button>
          <span className="play-deck__count">{String(active + 1).padStart(2, '0')} / {String(DECK.length).padStart(2, '0')}</span>
        </div>
      </div>

      <button type="button" className="play-deck__shuffle" onClick={() => shuffle(1)}>
        <Shuffle /> Shuffle the deck
      </button>
      <div className="play-deck__swatches" aria-label="Quick frame choices">
        {DECK.map((item, index) => (
          <button
            type="button"
            key={item.frame}
            aria-label={`Preview ${FRAME_META[item.frame].name}`}
            aria-pressed={active === index}
            onClick={() => setActive(index)}
            style={{ backgroundColor: item.palette.cardBg }}
          />
        ))}
      </div>
    </div>
  );
}
